import {
  Box,
  ContentTemplate,
  Grid,
  HeaderTemplate,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  SearchBar,
  SectionHeader,
  Stepper,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TitleHeader,
  Typography,
  useDataTable,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { useHistory } from 'react-router-dom';
import { MUIDataTableOptions, TableState } from 'web-library/stories/organism/DataTable/DataTable';
import NavigationButtons from '../../../components/NavigationButtons';
import key from '../../../routes/key';
import {
  PostCoordinatorSelectCandidateResponseData,
  usePostCoordinatorSelectCandidateListing,
} from '../../../api-refactored/coordinator-access-arrangement/usePostCoordinatorSelectCandidateListing';
import { ApplicationStatus } from '../../../modules/accessArrangment/components/candidate/AccessArrangements/AccessArrangementsModels';
import { candidateService } from '../../../api';
import { PaginatedData } from '../../../models/List';

const { accessArrangement } = key.child;
const { coordinatorRoot, coordinatorImportantNotes, coordinatorSelectCondition } = key[
  'access-arrangement'
].child;

interface SelectCandidateProps {
  routeSteps: { label: string }[];
}

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    left: '-5%',
  },
  radioButton: {
    padding: '0px',
    margin: '0px',
  },
  statement: {
    marginBottom: theme.spacing(4),
  },
  searchContainer: {
    paddingBottom: theme.spacing(3),
  },
  navigationButtonContainer: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
  },
  textDisabledForDraft: {
    color: theme.color.text.textDisabled,
  },
  errorText: {
    color: theme.color.state.stateError,
  },
}));

type examLevel = 'GCEA' | 'GCENA' | 'GCENT' | 'GCEO';

const getTableColumns = (t: any, state: TableState, classes: any) => {
  const tableColumns: MUIDataTableColumnDef[] = [
    {
      name: 'NRIC/FIN/PassportNo.',
      label: t('accessArrangement->>CoordinatorSelectCandidate.NRIC/FIN/PassportNo'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box
            className={
              state.data[dataIndex].status === ApplicationStatus.DRAFT
                ? classes.textDisabledForDraft
                : undefined
            }
          >
            <Typography variant="body2">{state.data[dataIndex].nricFin}</Typography>
          </Box>
        ),
      },
    },
    {
      name: 'name',
      label: t('accessArrangement->>CoordinatorSelectCandidate.CandidateName'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box
            className={
              state.data[dataIndex].status === ApplicationStatus.DRAFT
                ? classes.textDisabledForDraft
                : undefined
            }
          >
            <Typography variant="body2">{state.data[dataIndex].name}</Typography>
          </Box>
        ),
      },
    },
    {
      name: 'examLevels',
      label: t('accessArrangement->>CoordinatorSelectCandidate.ExamLevel'),
      options: {
        sort: false,
        searchable: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box
            className={
              state.data[dataIndex].status === ApplicationStatus.DRAFT
                ? classes.textDisabledForDraft
                : undefined
            }
          >
            <Typography noWrap variant="body2">
              {state.data[dataIndex].examLevels
                .map((d: examLevel) => {
                  switch (d) {
                    case 'GCEA':
                      return 'GCE A-LEVEL';
                    case 'GCEO':
                      return 'GCE O-LEVEL';
                    case 'GCENA':
                      return 'GCE N(A)-LEVEL';
                    case 'GCENT':
                      return 'GCE N(T)-LEVEL';
                    default:
                      return d;
                  }
                })
                .join(', ')}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'status',
      label: t('accessArrangement->>CoordinatorSelectCandidate.ApplicationStatus'),
      options: {
        sort: false,
        searchable: false,
        customBodyRenderLite: (dataIndex: number) => {
          if (state.data[dataIndex].status === ApplicationStatus.DRAFT) {
            return <Pill variant="info" label={state.data[dataIndex].status} />;
          }
          return <></>;
        },
      },
    },
  ];
  return tableColumns;
};

const CustomCheckbox = ({ props, classes }: { props: any; classes: Record<string,string> }) => (
  <Radio className={classes.radioButton} name="chkCandidate" size="medium" {...props} />
);
const SelectCandidate = ({ routeSteps }: SelectCandidateProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedCandidateId, setSelectedCandidateId] = useState<number[]>([]);
  const [errMsg, setErrMsg] = useState<boolean>(false);

  const {
    mutate: mutatePostCoordinatorSelectCandidateListing,
    isLoading,
  } = usePostCoordinatorSelectCandidateListing({
    onSuccess: (data: PaginatedData<PostCoordinatorSelectCandidateResponseData>) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'name',
    order: 'asc',
    size: 5,
    page: 0,
    search: '',
    filters: {},
  });

  const tableColumns = getTableColumns(t, state, classes);

  useEffect(() => {
    mutatePostCoordinatorSelectCandidateListing({
      q: state.search || '',
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        order: state.order,
        sort: state.sortColumn,
      },
    });
  }, [
    state.page,
    state.search,
    state.size,
    state.sortColumn,
    state.order,
    mutatePostCoordinatorSelectCandidateListing,
  ]);

  const customOptions: MUIDataTableOptions = {
    ...tOptions,
    selectableRows: 'single',
    selectToolbarPlacement: 'none',
    isRowSelectable: (dataIndex: number) => state.data[dataIndex].status !== ApplicationStatus.DRAFT,
    onRowSelectionChange: (currentRowsSelected: any, allRowsSelected: any, rowsSelected: any) => {
      if (tOptions.onRowSelectionChange) {
        // perform dataTable web-library onChange
        tOptions.onRowSelectionChange(currentRowsSelected, allRowsSelected, rowsSelected);
      }
      setSelectedCandidateId(rowsSelected || []);
    },
    sort: true,
    pagination: true,
    sortOrder: {
      name: state.sortColumn || 'name',
      direction: state.order || 'asc',
    },
  };

  useEffect(() => {
    tableDispatch({
      type: 'CLEAR_SELECTION',
    } as TableActionTypes);
  }, [tableDispatch, state.order, state.search]);

  const postRequest = candidateService.accessArrangement.candidate.usePostAppealArrangementCoordinatorCreateApplication(
    (res) => {
      const dataIndex = selectedCandidateId[0];
      const { name } = state.data[dataIndex];
      localStorage.setItem(`candidateNameNewApplication: ${res.data.uuid}`, name);
      history.push(
        `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorSelectCondition.route}/${res.data.uuid}`
      );
    },
    () => {
      // do nothing
    }
  );

  const handleSelectCandidate = () => {
    if (selectedCandidateId.length) {
      const dataIndex = selectedCandidateId[0];
      const { uuid } = state.data[dataIndex];
      postRequest.mutate({ uuid });
    } else {
      setErrMsg(true);
    }
  };

  const isDisabledProceedButton =
    !state.data.length ||
    !state.data.some(
      (d: PostCoordinatorSelectCandidateResponseData) => d.status !== ApplicationStatus.DRAFT
    );

  return (
    <>
      <TitleHeader
        title={t('accessArrangement->>AccessArrangementRoute.ApplyforAccessArrangements')}
      />
      <HeaderTemplate>
        <Box className={classes.stepper}>
          <Stepper activeStep={0} steps={routeSteps} />
        </Box>
      </HeaderTemplate>
      <ContentTemplate type="full">
        <SectionHeader
          title={t('accessArrangement->>CoordinatorSelectCandidate.SelectCandidate')}
        />
        <Box className={classes.statement}>
          {t('accessArrangement->>CoordinatorSelectCandidate.SelectCandidateMessage')}
        </Box>
        <Grid xs={12}>
          <Box>
            <SearchBar
              name="searchInput"
              ariaLabel="searchInput"
              placeholder={t('accessArrangement->>CoordinatorSelectCandidate.SearchPlaceHolder')}
              onSearch={(str) => {
                tableDispatch({
                  type: 'UPDATE_SEARCH',
                  payload: str.trim(),
                });
              }}
              className={classes.searchContainer}
              searchButton={!isMobile}
              clearInputButton
            />
          </Box>
        </Grid>

        <MUIDataTable
          // @ts-ignore
          isLoading={isLoading}
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns}
          // @ts-ignore
          data={state.data}
          emptyDataLabel={t('accessArrangement->>CoordinatorSelectCandidate.EmptyDataLabel')}
          options={customOptions}
          components={{
            Checkbox: (props: any) => CustomCheckbox({ props, classes }),
          }}
        />
        <Box className={classes.navigationButtonContainer}>
          {errMsg && (
            <Grid xs={12} md={8}>
              <Typography className={classes.errorText} display="inline" variant="body2">
                {t('accessArrangement->>CoordinatorSelectCandidate.EmptyCandidateError')}
              </Typography>
            </Grid>
          )}
          <NavigationButtons
            backLabel={t('accessArrangement->>CoordinatorSelectCandidate.Back')}
            onBackClick={() =>
              history.push(
                `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorImportantNotes.route}`
              )
            }
            onProceedClick={handleSelectCandidate}
            procceedLabel={t('accessArrangement->>CoordinatorSelectCandidate.ProceedWithCandidate')}
            isDisabledProceedButton={isDisabledProceedButton}
          />
        </Box>
      </ContentTemplate>
    </>
  );
};
export default SelectCandidate;
